// Return Generation API Service
// Endpoints: /api/v1/return-generation
// Refactored: Rule CRUD methods removed. Added compliance status report method.

import axiosInstance from '@/app/api/axiosInstance';
import {
  ReturnGenerationPreview,
  ReturnGenerationRunRequest,
  ReturnGenerationRunResponse,
  ReturnGenerationDashboard,
  ComplianceStatusReport,
  FileTaskRequest,
} from '@/app/types/compliance';

const API_PATH = '/return-generation';

// ============== Preview & Run ==============

/**
 * Preview what tasks would be generated (dry run)
 */
export const previewReturnGeneration = async (
  params?: {
    target_date?: string;
    rule_id?: number;
  }
): Promise<ReturnGenerationPreview> => {
  try {
    const response = await axiosInstance.get<ReturnGenerationPreview>(
      `${API_PATH}/preview`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to preview return generation:', error);
    throw error;
  }
};

/**
 * Execute return generation to create compliance tasks
 */
export const runReturnGeneration = async (
  data?: ReturnGenerationRunRequest
): Promise<ReturnGenerationRunResponse> => {
  try {
    const response = await axiosInstance.post<ReturnGenerationRunResponse>(
      `${API_PATH}/run`,
      data || {}
    );
    return response.data;
  } catch (error) {
    console.error('Failed to run return generation:', error);
    throw error;
  }
};

// ============== Dashboard ==============

/**
 * Get the compliance dashboard with summary counts
 */
export const getReturnGenerationDashboard = async (
  params?: {
    user_id?: number;
  }
): Promise<ReturnGenerationDashboard> => {
  try {
    const response = await axiosInstance.get<ReturnGenerationDashboard>(
      `${API_PATH}/dashboard`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get return generation dashboard:', error);
    throw error;
  }
};

// ============== Compliance Status ==============

/**
 * Get compliance status report for a given period
 */
export const getComplianceStatus = async (
  periodId: string,
  itemId?: number,
  statusFilter?: string
): Promise<ComplianceStatusReport> => {
  try {
    const params: Record<string, string | number> = { period_id: periodId };
    if (itemId !== undefined) {
      params.item_id = itemId;
    }
    if (statusFilter !== undefined) {
      params.status = statusFilter;
    }
    const response = await axiosInstance.get<ComplianceStatusReport>(
      `${API_PATH}/compliance-status`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get compliance status:', error);
    throw error;
  }
};

// ============== Task Filing ==============

/**
 * Mark a compliance task as filed
 */
export const fileComplianceTask = async (
  taskId: number,
  data: FileTaskRequest
): Promise<{ status: string; task_id: number; filed_date: string }> => {
  try {
    const response = await axiosInstance.post<{
      status: string;
      task_id: number;
      filed_date: string;
    }>(`${API_PATH}/tasks/${taskId}/file`, data);
    return response.data;
  } catch (error) {
    console.error(`Failed to file compliance task ${taskId}:`, error);
    throw error;
  }
};
